/*
  ============================================================
  Flying Dragon Nursery — Shipping Nudge + Rural Popup
  fdn-shipping.js
  ============================================================
  HOW IT WORKS
  ─────────────────────────────────────────────────────────────
  NUDGE BANNER
  • Reads cart total weight (grams) from data-total-weight on
    the injected div in mini-cart.liquid & main-cart-footer.liquid
  • 7,000g  = 1 small plant  (7kg)
  • 14,000g = 1 large plant (14kg) = fills one box
  • Box capacity = 14,000g
  • Shows "room for one more" when exactly 1 plant slot remains
    in the current or next box tier

  RURAL POPUP
  • Fires when customer clicks any .fdn-checkout-btn
  • Checks if current Shopify shipping rate contains "rural"
    (case-insensitive) via the Cart API
  • If rural: calculates boxes from weight, shows exact saving
  • $15/box urban · $20/box rural · $5 saving per box
  ============================================================
*/

(function () {
  'use strict';

  /* ── CONFIG ─────────────────────────────────────────────── */
  var CFG = {
    BOX_CAPACITY_G: 14000,   // grams per box
    SMALL_PLANT_G:   7000,   // small plant weight
    LARGE_PLANT_G:  14000,   // large plant / avocado
    URBAN_RATE:        15,   // NZD per box
    RURAL_RATE:        20,   // NZD per box
  };

  /* ── HELPERS ─────────────────────────────────────────────── */
  function boxCount(weightG) {
    return Math.ceil(weightG / CFG.BOX_CAPACITY_G) || 0;
  }

  function spaceLeftInCurrentTier(weightG) {
    // How many grams remain before filling the current box tier
    var remainder = weightG % CFG.BOX_CAPACITY_G;
    return remainder === 0 ? 0 : CFG.BOX_CAPACITY_G - remainder;
  }

  function getNudgeMessage(weightG) {
    if (weightG <= 0) return null;
    var space = spaceLeftInCurrentTier(weightG);
    // Exactly one small plant worth of space left in the box
    if (space === CFG.SMALL_PLANT_G) {
      return {
        icon: '📦',
        text: '<strong>Room for one more!</strong> Your shipping box still has space — add another plant and share the shipping cost, no extra charge.'
      };
    }
    return null;
  }

  /* ── NUDGE BANNER ────────────────────────────────────────── */
  function renderNudge(el) {
    if (!el) return;
    var weightG = parseInt(el.getAttribute('data-total-weight'), 10) || 0;
    var msg = getNudgeMessage(weightG);
    if (!msg) {
      el.style.display = 'none';
      return;
    }
    el.innerHTML =
      '<div class="fdn-nudge-banner">' +
        '<span class="fdn-nudge-icon" aria-hidden="true">' + msg.icon + '</span>' +
        '<span class="fdn-nudge-text">' + msg.text + '</span>' +
      '</div>';
    el.style.display = 'block';
  }

  function initNudgeBanners() {
    ['fdn-nudge-drawer', 'fdn-nudge-page'].forEach(function (id) {
      renderNudge(document.getElementById(id));
    });
  }

  /* ── RURAL POPUP ─────────────────────────────────────────── */
  var ruralPopupShown = false;

  function isRuralRate(rateName) {
    return /rural/i.test(rateName || '');
  }

  function buildPopup(boxes, saving) {
    var ruralTotal  = (boxes * CFG.RURAL_RATE).toFixed(2);
    var urbanTotal  = (boxes * CFG.URBAN_RATE).toFixed(2);
    var savingFmt   = saving.toFixed(2);
    var boxWord     = boxes === 1 ? 'box' : 'boxes';
    var plural      = boxes === 1 ? '' : 'es';

    var overlay = document.createElement('div');
    overlay.id = 'fdn-rural-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'fdn-rural-title');

    overlay.innerHTML =
      '<div class="fdn-rural-popup">' +
        '<div class="fdn-rural-popup__top">' +
          '<div class="fdn-rural-popup__icon" aria-hidden="true">📍</div>' +
          '<button class="fdn-rural-popup__close" id="fdn-rural-close" aria-label="Close">&#x2715;</button>' +
        '</div>' +
        '<h2 class="fdn-rural-popup__title" id="fdn-rural-title">You\'re on rural delivery</h2>' +
        '<p class="fdn-rural-popup__body">' +
          'Your postcode is registered as a <strong>rural address</strong>. ' +
          'Your order needs <strong>' + boxes + ' ' + boxWord + '</strong> — rural delivery applies to each box' + plural + '.' +
        '</p>' +
        '<div class="fdn-rural-popup__saving">' +
          '<span class="fdn-rural-popup__saving-icon" aria-hidden="true">💡</span>' +
          '<span>Using an <strong>urban address</strong> — a workplace, family member, or a parcel locker — ' +
          'would save you <strong>$' + savingFmt + ' NZD</strong> on this order ' +
          '(' + boxes + ' box' + plural + ' × $5 surcharge).</span>' +
        '</div>' +
        '<div class="fdn-rural-popup__actions">' +
          '<button class="fdn-rural-popup__cta" id="fdn-rural-change">Change to an urban address</button>' +
          '<button class="fdn-rural-popup__skip" id="fdn-rural-skip">' +
            'Continue with rural delivery ($' + ruralTotal + ')' +
          '</button>' +
        '</div>' +
      '</div>';

    document.body.appendChild(overlay);

    // Close helpers
    function closePopup() {
      overlay.remove();
      ruralPopupShown = false;
    }

    document.getElementById('fdn-rural-close').addEventListener('click', closePopup);
    document.getElementById('fdn-rural-skip').addEventListener('click', function () {
      closePopup();
      // Let checkout proceed — submit the form
      var form = document.querySelector('form[action*="cart"]');
      if (form) form.submit();
    });
    document.getElementById('fdn-rural-change').addEventListener('click', function () {
      closePopup();
      // Scroll to address field or just close so they can update
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    // Close on backdrop click
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closePopup();
    });

    // Trap focus
    overlay.querySelector('.fdn-rural-popup__close').focus();
  }

  function checkRuralAndIntercept(e, weightG) {
    if (ruralPopupShown) return; // already shown, let checkout proceed
    e.preventDefault();

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // cart.shipping_rate is set once customer has entered address
        // We also check via the stored rate title if available
        var rateName = (cart.shipping_rate && cart.shipping_rate.title) || '';

        // Fallback: check sessionStorage for previously detected rural rate
        if (!rateName) {
          rateName = sessionStorage.getItem('fdn_last_rate') || '';
        }

        if (isRuralRate(rateName)) {
          ruralPopupShown = true;
          var boxes   = boxCount(weightG);
          var saving  = boxes * (CFG.RURAL_RATE - CFG.URBAN_RATE);
          buildPopup(boxes, saving);
        } else {
          // Not rural or can't detect — let checkout proceed normally
          var form = e.target.closest('form');
          if (form) {
            // Remove our listener temporarily to avoid loop
            form.submit();
          } else {
            window.location.href = '/checkout';
          }
        }
      })
      .catch(function () {
        // On any error just let them check out
        var form = e.target.closest('form');
        if (form) form.submit();
      });
  }

  function initRuralPopup() {
    document.querySelectorAll('.fdn-checkout-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        // Get weight from the nearest nudge container
        var weightEl =
          document.getElementById('fdn-nudge-drawer') ||
          document.getElementById('fdn-nudge-page');
        var weightG = weightEl
          ? parseInt(weightEl.getAttribute('data-total-weight'), 10) || 0
          : 0;
        checkRuralAndIntercept(e, weightG);
      });
    });
  }

  /* ── INIT ────────────────────────────────────────────────── */
  function init() {
    initNudgeBanners();
    initRuralPopup();

    // Re-run nudge if cart drawer updates (Be Yours fires custom events)
    document.addEventListener('cart:updated', initNudgeBanners);
    document.addEventListener('cartdrawer:opened', initNudgeBanners);
    document.addEventListener('cart:refresh', initNudgeBanners);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
